import { jsx as _jsx } from "react/jsx-runtime";
import { createContext, useContext, useCallback, useMemo } from 'react';
import { useQuery } from '@tanstack/react-query';
import { useTrainContext } from './TrainContext';
import { trainQueryKeys } from '../internal/queryKeys';
export const NetworksContext = createContext(null);
export function useNetworksContext() {
    const ctx = useContext(NetworksContext);
    if (!ctx) {
        throw new Error('useNetworksContext must be used within a <TrainProvider>');
    }
    return ctx;
}
export function NetworksProvider({ children, initialNetworks, initialPrices }) {
    const { apiClient } = useTrainContext();
    const networksQuery = useQuery({
        queryKey: trainQueryKeys.networks(),
        queryFn: () => apiClient.getNetworks(),
        staleTime: 5 * 60000,
        initialData: initialNetworks,
    });
    const pricesQuery = useQuery({
        queryKey: trainQueryKeys.prices(),
        queryFn: () => apiClient.getPrices(),
        staleTime: 60000,
        retry: false,
        initialData: initialPrices,
    });
    const refetchNetworks = useCallback(async () => {
        await networksQuery.refetch();
    }, [networksQuery]);
    const refetchPrices = useCallback(async () => {
        await pricesQuery.refetch();
    }, [pricesQuery]);
    // Build O(1) lookup map keyed by uppercased CAIP-2 ID
    const networkMap = useMemo(() => {
        const map = new Map();
        for (const n of networksQuery.data ?? []) {
            map.set(n.caip2Id, n);
        }
        return map;
    }, [networksQuery.data]);
    const value = useMemo(() => ({
        networks: networksQuery.data ?? [],
        networkMap,
        prices: pricesQuery.data ?? {},
        isLoading: networksQuery.isLoading,
        error: networksQuery.error instanceof Error ? networksQuery.error : networksQuery.error ? new Error(String(networksQuery.error)) : null,
        refetchNetworks,
        refetchPrices,
    }), [networksQuery.data, networkMap, networksQuery.isLoading, networksQuery.error, pricesQuery.data, refetchNetworks, refetchPrices]);
    return (_jsx(NetworksContext.Provider, { value: value, children: children }));
}
